import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Pressable, View, StyleSheet, Text, TextInput, Alert, ActivityIndicator, } from 'react-native';
import imageCompression from 'browser-image-compression';
import { useUploadPhotoMutation } from '../services/abbiWayApi';
import { PhotoType } from '../types/photo.interface';
import { Button } from '../components/Button';
import { Glyph } from '../components/Glyph';
import { shadowStyle } from '../components/styles';
import { capitalize } from '../utils/string';
import { theme } from '../theme';
export const PhotoUploadModal = ({ visible, onClose, userIdToUploadFor, fullScreen = false, }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [photoType, setPhotoType] = useState(PhotoType.PROGRESS);
    const [caption, setCaption] = useState('');
    const [isCompressing, setIsCompressing] = useState(false);
    const [compressionError, setCompressionError] = useState(null);
    const [uploadPhoto, { isLoading, error: uploadError, reset: resetMutation }] = useUploadPhotoMutation();
    const resetForm = useCallback(() => {
        setSelectedFile(null);
        setPhotoType(PhotoType.PROGRESS);
        setCaption('');
        setCompressionError(null);
    }, []);
    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
        }
    };
    const compressImage = async (imageFile) => {
        setIsCompressing(true);
        setCompressionError(null);
        console.log('Original file size:', imageFile.size / 1024 / 1024, 'MB');
        const options = {
            maxSizeMB: 10, // (max file size in MB)
            maxWidthOrHeight: 1080, // (max width or height in pixels)
            useWebWorker: true,
            fileType: 'image/jpeg', // Force JPEG output
        };
        try {
            // imageCompression returns a File object, which is a Blob.
            // We'll create a new File from it to ensure the filename is correct.
            const compressedFileBlob = await imageCompression(imageFile, options);
            const originalName = imageFile.name;
            // Get the base name, removing the old extension.
            const baseName = originalName.substring(0, originalName.lastIndexOf('.')) ||
                originalName;
            const newFileName = `${baseName}.jpeg`;
            const newFile = new File([compressedFileBlob], newFileName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
            });
            console.log('Compressed file size:', newFile.size / 1024 / 1024, 'MB');
            return newFile;
        }
        catch (error) {
            console.error('Image compression failed:', error);
            setCompressionError('Image compression failed. Please try a smaller file or a different format.');
            return null; // Return null if compression fails
        }
        finally {
            setIsCompressing(false);
        }
    };
    const handleSubmit = async () => {
        if (!selectedFile) {
            Alert.alert('No file selected', 'Please select a photo to upload.');
            return;
        }
        const fileToUpload = await compressImage(selectedFile);
        if (!fileToUpload) {
            console.error('Image compression failed');
            return;
        }
        // We now handle the result of the mutation directly, without using .unwrap().
        // This is a more robust way to handle errors and ensures the `isLoading`
        // state transitions correctly from true to false, even on failure.
        const result = await uploadPhoto({
            file: fileToUpload,
            photoType,
            caption,
            forUserId: userIdToUploadFor,
        });
        if ('data' in result) {
            // Success case
            onClose(); // Close modal on success, which will trigger reset via useEffect
        }
        else if ('error' in result) {
            // Error case. The `uploadError` state from the hook is now populated automatically.
            console.error('Failed to upload photo:', result.error);
            Alert.alert('Upload Error', result.error?.data?.message ||
                'An error occurred while uploading the photo.');
        }
    };
    // Reset state when modal is closed or opened
    useEffect(() => {
        if (!visible) {
            resetForm();
            resetMutation(); // Also reset mutation state on close
        }
    }, [visible, resetForm, resetMutation]);
    return (<Modal animationType="slide" transparent={true} visible={visible} onRequestClose={onClose}>
      <View style={[styles.centeredView, fullScreen && styles.centeredFull]}>
        <View style={[styles.modalView, fullScreen && styles.modalFull]}>
          <View style={styles.titleRow}>
            <Text style={styles.modalTitle}>Upload Photo</Text>
            {fullScreen ? (<Pressable onPress={onClose} style={styles.closeHit} accessibilityRole="button" accessibilityLabel="Close upload">
                <Glyph name="close" size={22} color={theme.colors.textCaption}/>
              </Pressable>) : null}
          </View>

          {/* For React Native Web, a standard input type="file" works */}
          <input type="file" accept="image/*" onChange={handleFileChange} style={fileInputStyle}/>
          {selectedFile && <Text>Selected: {selectedFile.name}</Text>}

          <Text style={styles.label}>Photo Type:</Text>
          <select value={photoType} onChange={e => setPhotoType(e.target.value)} style={styles.pickerStyle}>
            {Object.values(PhotoType).map(pt => (<option key={pt} value={pt}>
                {capitalize(pt)}
              </option>))}
          </select>

          <Text style={styles.label}>Caption (Optional):</Text>
          <TextInput style={styles.input} onChangeText={setCaption} value={caption} placeholder="Enter a caption"/>

          {(isLoading || isCompressing) && (<ActivityIndicator size="large" color={theme.colors.textLinkBrand}/>)}
          {compressionError && (<Text style={styles.errorText}>{compressionError}</Text>)}
          {uploadError && (<Text style={styles.errorText}>
              Upload Error:{' '}
              {uploadError?.data?.message ||
                'An unknown error occurred during upload.'}
            </Text>)}

          <View style={styles.buttonContainer}>
            <Button title="Cancel" onPress={onClose}/>
            <Button title="Upload" onPress={handleSubmit} // This will now trigger compression first
     disabled={isLoading || isCompressing || !selectedFile}/>
          </View>
        </View>
      </View>
    </Modal>);
};
const styles = StyleSheet.create({
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.overlayModal,
    },
    modalView: {
        margin: 20,
        backgroundColor: theme.colors.surfaceElevated,
        borderRadius: 20,
        padding: 35,
        alignItems: 'center',
        ...shadowStyle(0, 2, 4, 0.25),
        elevation: 5,
        width: '80%',
        maxWidth: 500,
    },
    modalTitle: { fontSize: 20, marginBottom: 15, fontWeight: 'bold' },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        alignSelf: 'stretch',
    },
    closeHit: {
        minWidth: 44,
        minHeight: 44,
        marginTop: -12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    centeredFull: {
        justifyContent: 'flex-start',
        alignItems: 'stretch',
    },
    modalFull: {
        flex: 1,
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        borderRadius: 0,
        padding: 16,
        alignItems: 'stretch',
    },
    label: { marginTop: 10, marginBottom: 5, alignSelf: 'flex-start' },
    input: {
        height: 40,
        borderColor: theme.colors.controlNeutral,
        borderWidth: 1,
        marginBottom: 10,
        paddingHorizontal: 10,
        width: '100%',
        borderRadius: 5,
    },
    pickerStyle: {
        height: 40,
        width: '100%',
        marginBottom: 10,
        borderWidth: 1,
        borderColor: theme.colors.controlNeutral,
        borderRadius: 5,
        paddingHorizontal: 10,
    },
    buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        width: '100%',
        marginTop: 20,
    },
    errorText: { color: theme.colors.dangerText, marginTop: 10 },
});
/** Keeps the browser's file picker inside a phone-width sheet. */
const fileInputStyle = { maxWidth: '100%' };
export default PhotoUploadModal;
